import axios from 'axios';
import { Platform } from 'react-native';
import * as Device from 'expo-device';
import * as Application from 'expo-application';
import {getUniqueId} from 'react-native-device-info'
import AsyncStorage from '@react-native-async-storage/async-storage';
import {store} from '../redux/store';
import {reportError} from '../utils/reportError';

import { BASE_URL } from './constants';
import { E_STORAGE_KEYS } from '../@types';
import isWeb from '../utils/isWeb';
import { getNewToken } from './people';
import getConfig, { PROD_CONFIG } from '../utils/getConfig';

export const residentApi = () => {
  return axios.create({
    baseURL: BASE_URL,
  });
};

let resident = axios.create({
  baseURL: BASE_URL,
});

let residentInterceptorsAttached = false;

export const residentApiWithAuth = () => {
  if (!residentInterceptorsAttached) {
    resident.interceptors.request.use(async requestConfig => {
      requestConfig.metadata = {
        startTime: Date.now(),
      };
      return injectAuthHeaders(requestConfig);
    }, rejectWithError);

    resident.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config || {};
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { token } = await getNewToken();
            resident.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return resident(originalRequest);
          } catch (refreshError) {
            reportError(refreshError, {
              feature: 'network',
              action: 'token refresh retry',
              request: {
                method: originalRequest?.method,
                url: originalRequest?.url,
                status: error.response?.status,
                retry: true,
                durationMs: Date.now() - (originalRequest?.metadata?.startTime || Date.now()),
              },
            });
            AsyncStorage.removeItem(E_STORAGE_KEYS.TOKEN_PERSIST).then(() => {
              store.dispatch({ type: E_STORAGE_KEYS.CLEAR_REDUX_PERSIST_ROOT });
            });
          }
          return Promise.reject(error);
        }
        reportError(error, {
          feature: 'network',
          action: 'axios request',
          request: {
            method: originalRequest?.method,
            url: originalRequest?.url,
            status: error.response?.status,
            retry: Boolean(originalRequest?._retry),
            durationMs: Date.now() - (originalRequest?.metadata?.startTime || Date.now()),
          },
        });
        return Promise.reject(error);
      }
    );

    if (!isWeb) {
      resident.interceptors.request.use(injectDeviceInfoHeaders, rejectWithError);
    }

    residentInterceptorsAttached = true;
  }

  return resident;
};

async function injectAuthHeaders(requestConfig) {
  try {
    const tokenString = await AsyncStorage.getItem(E_STORAGE_KEYS.AUTH_TOKEN);
    if (tokenString) {
      const { token } = JSON.parse(tokenString);
      if (token) requestConfig.headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {}
  return requestConfig;
}

async function injectDeviceInfoHeaders(requestConfig) {
  const deviceName = Device.deviceName ?? 'Unknown Device';
  let uniqueId = await getUniqueId() || 'Unknown';
  const bundleId = getConfig("BUNDLE_ID");
  
  requestConfig.headers['DeviceName'] = `${deviceName}-${uniqueId}`;
  requestConfig.headers['UniqueId'] = uniqueId;
  requestConfig.headers['UserAgent'] = deviceName;
  requestConfig.headers['AppName'] = PROD_CONFIG.BUNDLE_ID;
  // console.log('Injected Device Info Headers:', requestConfig.headers);
  return requestConfig;
}

async function rejectWithError(error) {
  return Promise.reject(error);
}
